import { GET } from "@/app/api/health/route";
import language from "@/templates/Admin/variants/default/utils/services/language";
import { ColorManagement } from "three";

// Helper function to add id_path parameter to project endpoints
const withIdPath = (endpoint: string, id_path?: string) => {
    const separator = endpoint.includes('?') ? '&' : '?';
    return id_path ? `${endpoint}${separator}id_path=${encodeURIComponent(id_path)}` : endpoint;
};

const apiEndPointList = {
    auth: {
        LOGIN: (id_path?: string) => withIdPath("/v1/projects/users/login", id_path),
        VERIFY_OTP: (id_path?: string) => withIdPath("/v1/projects/users/verify-otp", id_path),
        REGISTER: (id_path?: string) => withIdPath("/v1/projects/users", id_path),
        LOGOUT: (id_path?: string) => withIdPath("/v1/projects/users/logout", id_path),
        VERIFY_EMAIL: (id_path?: string) => withIdPath("/v1/projects/users/verify-email", id_path),
        REFRESH_ACCESS_TOKEN: (id_path?: string) => withIdPath("/v1/projects/users/refresh-token", id_path),
        VERIFY_ACCOUNT: (id_path?: string) => withIdPath("/v1/projects/users/verify-email", id_path),
        FORGOT_PASSWORD: (id_path?: string) => withIdPath("/v1/projects/users/forgot-password", id_path),
    },
    authGeneral: {
        LOGIN: "/v1/general/auth/signin",
        VERIFY_OTP: "/v1/general/auth/verify-otp",
        REGISTER: "/v1/general/auth/signup",
        VERIFY_PASSWORD: "/v1/general/auth/verify-password",
        REQUEST_RESET_PASSWORD: "/v1/general/auth/request-reset-password",
        REQUEST_RESET_PASSWORD_ADMIN: "/v1/general/auth/request-reset-password-admin",
        RESET_PASSWORD: "/v1/general/auth/reset-password",
        SEND_2FA_CODE: "/v1/general/2fa/login/send-otp",
        VERIFY_2FA: "/v1/general/2fa/login/verify",
        LOGOUT: "/v1/general/auth/logout",
        VERIFY_EMAIL: (token: string) => `/v1/general/auth/verify-email/${token}`,
        REQUEST_VERIFY_EMAIL: "/v1/general/auth/request-verify-email",
        REFRESH_ACCESS_TOKEN: "/v1/general/auth/refresh-token"
    },
    adminGeneral: {
        PROJECT_STATISTICS: "/v1/general/project-statistics",
        PROJECT_INITIALIZATION: "/v1/general/project-initialization",
        NEXT_PROJECT_ID: "/v1/general/project-initialization/next-id",
        NEW_PLATFORM_PROJECTS: "/v1/general/project-statistics/new-platform",
        GET_NAME_PATH_BY_ID: (projectId: string) => `/v1/general/project-statistics/name-path/${projectId}`,
        PROJECT_SETUP_COMPLETE: "/v1/general/project-setup/complete",
    },
    idPath: {
        GET_ID_BY_NAME_PATH: (name_path: string) => `/v1/general/project/project/?path=${encodeURIComponent(name_path)}`,
        GET_ID_BY_FULL_NAME_PATH: (name_path: string) => `/v1/general/project/full-path/?path=${encodeURIComponent(name_path)}`,
        // GET_TOUR_BY_ID_PATH: (id_path: string) => `/v1/general/project/project?path=${id_path}`
        GET_TOUR_BY_ID_PATH: (id_path: string) => `/v1/projects/settings/values/by-uuid/tour_xml/?id_path=${encodeURIComponent(id_path)}`, // Get tour XML by setting UUID
        GET_SETTING_BY_UUID: (id_path: string, uuid: string, link_id?: string, language_id = "vn") => {
            const params = new URLSearchParams({
                id_path,
                language_id,
            });
            if (link_id) params.set("link_id", link_id);
            return `/v1/projects/settings/values/by-uuid/${encodeURIComponent(uuid)}/?${params.toString()}`;
        },
        GET_ALL_VARIABLE_SETTING_AND_VALUE: (id_path: string, sublink: string) => withIdPath(`/v1/projects/settings/categories/1/key-value/?link_id=${encodeURIComponent(sublink)}`, id_path),
        GET_ALL_VARIABLE_SETTING_AND_VALUE_FALLBACK: (id_path: string, sublink: string) => withIdPath(`/v1/projects/settings/categories/1/key-value-fallback/?link_id=${encodeURIComponent(sublink)}`, id_path),
        GET_ALL_VARIABLE_SETTING_AND_VALUE_FALLBACK_BY_CATEGORY: (id_path: string, sublink: string, category: string | number) => withIdPath(`/v1/projects/settings/categories/${encodeURIComponent(category)}/key-value-fallback/?link_id=${encodeURIComponent(sublink)}`, id_path),
    },

    analytics: {
        GET_PLAUSIBLE_SCRIPT: (id_path: string) => `/v1/projects/plausible-script?id_path=${encodeURIComponent(id_path)}`,
    },

    audio: {
        GET_DESCRIPTION_ALL_SCENES: (lang: string) => `/v1/general/auth/getDescriptionAllScenes${lang}`,
        GET_DESCRIPTION_ALL_GROUPS: (lang: string) => `/v1/general/auth/getDescriptionAllScenes${lang}`,
        GET_CHECK_VOICE_MODE: (id_path: string, link_id?: string | number) => {
            const base = withIdPath(`v1/projects/voice/check-playback`, id_path);
            return (link_id !== undefined && link_id !== null) ? `${base}&link_id=${encodeURIComponent(link_id)}` : base;
        },

    },

    metadata: {

        GET_METADATA: (id_path: string) => `/v1/general/auth/google${id_path}`,
    },
    hotspot: {
        GET_HOTSPOT_BY_SCENE_NAME: (sceneName: string, id_path: string) => `/v1/projects/hotspot/scene-name/${sceneName}?id_path=${id_path}`,
    },
    scene: {
        GET_PREVIOUS_SCENE_BY_SCENE_NAME: (sceneName: string, id_path: string, language_id: string) => withIdPath(`/v1/projects/scene/name/${sceneName}/previous&language_id=${language_id}`, id_path),
        GET_NEXT_SCENE_BY_SCENE_NAME: (sceneName: string, id_path: string, language_id: string) => withIdPath(`/v1/projects/scene/name/${sceneName}/next&language_id=${language_id}`, id_path),
    },

    cmc: {
        UPLOAD_TO_S3: (id_path?: string) => `/v1/general/cmc/upload?id_path=${id_path}`,
        UPLOAD_FOLDER_TO_S3: "/v1/general/cmc/upload-folder",
        LIST_OBJECTS: "/v1/general/cmc/list-objects",
        DELETE_OBJECTS: "/v1/general/cmc/delete-objects",
        CREATE_FOLDER: "/v1/general/cmc/create-folder",
        RENAME_OBJECT: "/v1/general/cmc/rename-object",
        UPLOAD_FILE_S3: `/v1/general/cmc/upload`,
        RESOLVE_TOUR_XML: `/v1/general/cmc/resolve-tour-xml`,

        // Presigned upload session (KRPano folder upload)
        UPLOAD_SESSION_CREATE: "/v1/general/cmc/upload-sessions",
        UPLOAD_SESSION_PRESIGN: (id: string) => `/v1/general/cmc/upload-sessions/${id}/presign`,
        UPLOAD_SESSION_CONFIRM: (id: string) => `/v1/general/cmc/upload-sessions/${id}/confirm`,
        UPLOAD_SESSION_REPORT_ERRORS: (id: string) => `/v1/general/cmc/upload-sessions/${id}/report-errors`,
        UPLOAD_SESSION_MULTIPART_INITIATE: (id: string) => `/v1/general/cmc/upload-sessions/${id}/multipart/initiate`,
        UPLOAD_SESSION_MULTIPART_PRESIGN_PARTS: (id: string) => `/v1/general/cmc/upload-sessions/${id}/multipart/parts/presign`,
        UPLOAD_SESSION_MULTIPART_ACK_PARTS: (id: string) => `/v1/general/cmc/upload-sessions/${id}/multipart/parts/ack`,
        UPLOAD_SESSION_MULTIPART_COMPLETE: (id: string) => `/v1/general/cmc/upload-sessions/${id}/multipart/complete`,
        UPLOAD_SESSION_MULTIPART_ABORT: (id: string) => `/v1/general/cmc/upload-sessions/${id}/multipart/abort`,
        UPLOAD_SESSION_GET: (id: string) => `/v1/general/cmc/upload-sessions/${id}`,
        UPLOAD_SESSION_INCOMPLETE: "/v1/general/cmc/upload-sessions/incomplete",
        UPLOAD_SESSION_FINALIZE: (id: string) => `/v1/general/cmc/upload-sessions/${id}/finalize`,
        UPLOAD_SESSION_CANCEL: (id: string) => `/v1/general/cmc/upload-sessions/${id}/cancel`,
        UPLOAD_SESSION_CORS_PROBE_PRESIGN: "/v1/general/cmc/upload-sessions/cors-probe/presign",
        UPLOAD_SESSION_CORS_PROBE_VERIFY: "/v1/general/cmc/upload-sessions/cors-probe/verify",

        // Scan missing .tiles folders not indexed in DB
        SCAN_MISSING_TILES_FOLDERS: "/v1/general/cmc/scan-missing-tiles-folders",

        // Trash Bin Management
        MOVE_TO_TRASH: "/v1/general/cmc/move-to-trashbin",
        LIST_TRASH: "/v1/general/cmc/list-trashbin",
        RESTORE_FROM_TRASH: "/v1/general/cmc/restore-from-trashbin",
        PERMANENT_DELETE: "/v1/general/cmc/permanent-delete",
        EMPTY_TRASH: "/v1/general/cmc/empty-trashbin",
        GET_TRASH_STATS: "/v1/general/cmc/trash-stats"
    },

    language: {
        GET_ALL_LANGUAGE_BY_ID_PATH: (id_path?: string) => withIdPath(`v1/projects/language`, id_path),
        UPLOAD_TO_S3: "/v1/general/cmc/upload",
        UPLOAD_BASE64_TO_S3: "/v1/general/cmc/upload-base64",
        GET_LANGUAGE_DEFAULT: (id_path?: string) => withIdPath(`v1/projects/language/default`, id_path),
    },

    color: {
        // GET_COLOR_BY_ID: (id: string) => `/v1/general/color/${id}`,
        // GET_ALL_COLORS: () => `/v1/general/color`,
    },

    checkin: {
        REMOVE_BACKGROUND: (id_path: string) => withIdPath("/v1/projects/rembg/remove-background", id_path),
    },

    ColorManagement: {
        GET_ALL_COLOR_BY_VERSION: (id_path?: string, version_id?: string) => `v1/projects/color/versions/${version_id}/key-values?id_path=${id_path}`,
        CREATE_COLOR: (id_path?: string) => withIdPath(`v1/projects/colors`, id_path),
        UPDATE_COLOR: (color_id: number, id_path?: string) => withIdPath(`v1/projects/colors/${color_id}`, id_path),
        DELETE_COLOR: (color_id: number, id_path?: string) => withIdPath(`v1/projects/colors/${color_id}`, id_path),
    },

    functionCalling: {
        // Knowledge and Web Search
        RETRIEVE_KNOWLEDGE_BASE: "/v1/general/function-calling/retrieve-knowledge-base",
        WEB_SEARCH: "/v1/general/function-calling/web-search",
        RETRIEVE_WEB_CONTENT: "/v1/general/function-calling/retrieve-web-content",
        GET_SECTORS: "/v1/general/function-calling/sectors",
        OPEN_SECTOR_LIST: "/v1/general/function-calling/sectors/open",
        CLOSE_SECTOR_LIST: "/v1/general/function-calling/sectors/close",
        OPEN_SECTOR_PROJECT_LIST: "/v1/general/function-calling/sector-project-list/open",
        CLOSE_SECTOR_PROJECT_LIST: "/v1/general/function-calling/sector-project-list/close",
        DISCONNECT_AGENT: "/v1/general/function-calling/agent/disconnect",

        // Project Management
        LIST_PROJECT_NAMES: "/v1/general/function-calling/projects/names",
        SEARCH_PROJECT_ID: "/v1/general/function-calling/projects/search-id",
        GET_PROJECT_NAME: "/v1/general/function-calling/projects/get-name",
        GET_PROJECTS_BY_LOCATION: "/v1/general/function-calling/projects/location",
        OPEN_PROJECT: "/v1/general/function-calling/projects/open",

        // Scene Management
        LIST_SCENE_NAMES: "/v1/general/function-calling/scenes/list-names",
        SEARCH_SCENE_NAME: "/v1/general/function-calling/scenes/search-name",
        GET_SCENE_GROUP: "/v1/general/function-calling/scenes/get-group",
        GET_GROUP_INFO: "/v1/general/function-calling/scenes/group-info",
        SEARCH_GROUP_INFO: "/v1/general/function-calling/scenes/search-group-info",
        GET_MODELS_IN_CURRENT_SCENE: "/v1/general/function-calling/scenes/get-models",
        GET_SCENES_IN_GROUP_SCENE: "/v1/general/function-calling/scenes/scenes-in-group",

        // Asset Management
        GET_ALL_MODEL_NAMES: "/v1/general/function-calling/assets/models/names",
        GET_ALL_BOOK_NAMES: "/v1/general/function-calling/assets/books/names",
        GET_ALL_PHOTO_NAMES: "/v1/general/function-calling/assets/photos/names",
        GET_ALL_VIDEO_NAMES: "/v1/general/function-calling/assets/videos/names",

        // Asset Search
        SEARCH_MODEL_ID: "/v1/general/function-calling/assets/models/search-not-socket",
        SEARCH_BOOK_ID: "/v1/general/function-calling/assets/books/search",
        SEARCH_PHOTO_ID: "/v1/general/function-calling/assets/photos/search",
        SEARCH_VIDEO_ID: "/v1/general/function-calling/assets/videos/search",
        GET_ASSET_INFO: "/v1/general/function-calling/assets/info",

        // Open Asset
        OPEN_MODEL: "/v1/general/function-calling/assets/model/open-not-socket",
        OPEN_BOOK: "/v1/general/function-calling/assets/book/open-not-socket",
        OPEN_PHOTO: "/v1/general/function-calling/assets/photo/open-not-socket",
        OPEN_VIDEO: "/v1/general/function-calling/assets/video/open-not-socket",

        // Navigation and Interaction
        NAVIGATE_TO_SCENE: "/v1/general/function-calling/navigation/scene-not-socket",
        GET_CURRENT_SCENE_INFO: "/v1/general/function-calling/navigation/current-scene-not-socket",

        // Virtual Tour
        CREATE_VIRTUAL_TOUR: "/v1/general/function-calling/tours/create-not-socket",
        SAVE_VIRTUAL_TOUR: "/v1/general/function-calling/tours/save",

        // Webcam
        GET_WEBCAM_DESCRIPTION: "/v1/general/function-calling/webcam/description",

        // Hybrid pipeline: retrieve knowledge for focused objects in 360° visual context
        RETRIEVE_OBJECT_KNOWLEDGE: "/v1/general/function-calling/retrieve-object-knowledge",
    },

    // Visitor Tracking
    visitorTracking: {
        JOIN_ROOM: "/v1/project/visitor-tracking",
        GET_STATS: (roomId: string) => `/api/visitor-tracking?roomId=${encodeURIComponent(roomId)}`,
        GET_TOTAL_VISITS: (roomId: string) => `/v1/project/visitor-tracking/reports/total-summary?roomId=${encodeURIComponent(roomId)}`,
        GET_VISITOR_INFO_ALL_ROOMS: "/v1/project/visitor-tracking/all-rooms",
    },

    project: {
        GET_ALL_PROJECTS: "/v1/projects/projects",
        GET_PROJECT_FIELDS: "/v1/projects/projects/fields",
        GET_PROJECT_BY_ID: (id: string) => `/v1/projects/projects/${id}`,
        SEARCH_PROJECT: (keyword: string) => `/v1/projects/projects/search/${keyword}`,
        GET_PROJECT_BY_FIELD: (field: string) => `/v1/projects/projects/field/${field}`,
        GET_PROJECT_BY_TAG: (tag: string) => `/v1/projects/projects/tag/${tag}`,
        GET_NAME_PATH_BY_ID: (projectId: string, id_path?: string) => withIdPath(`/v1/projects/project-statistics/name-path/${projectId}`, id_path),
    },
    service: {
        TRANSLATE: "/v1/general/services/translate",
        TEXT_TO_SPEECH: "/v1/general/services/tts",
        LIST_VOICES: "/v1/general/services/voices",
        VOICE_PREVIEW: (voiceId: string) => `/v1/general/services/voices/preview?voice_id=${encodeURIComponent(voiceId)}`,
        TTS_OPTIONS: (provider: string) => `/v1/general/services/tts/options?provider=${encodeURIComponent(provider)}`,
        VOICE_PRESETS: (id_path: string) => withIdPath("/v1/projects/voice-presets", id_path),
        VOICE_PRESET: (id_path: string, id: string | number) => withIdPath(`/v1/projects/voice-presets/${id}`, id_path),
        TRANSCRIBE_VOICE_PRESET: (id_path: string) => withIdPath("/v1/projects/voice-presets/transcribe", id_path),
        TTS_DICTIONARY: (id_path: string) => withIdPath("/v1/projects/tts-dictionary", id_path),
        TTS_DICTIONARY_ITEM: (id_path: string, id: string | number) => withIdPath(`/v1/projects/tts-dictionary/${id}`, id_path),
    },
    link: {
        GET_ALL_LINK: (id_path: string) => withIdPath("/v1/projects/sublink", id_path),
        GET_LINK_BY_NAME: (id_path: string, name: string) => withIdPath(`/v1/projects/sublink/by-name/${name}`, id_path),
    },
    module: {
        GET_ALL_MODULES_USER: (id_path: string) => `/v1/projects/modules/user?id_path=${id_path}`,
        GET_ALL_MODULES_ADMIN: (id_path: string) => `/v1/projects/modules/admin?id_path=${id_path}`,
        GET_ALL_MODULES: (id_path: string) => `/v1/projects/modules/all?id_path=${id_path}`

    },

    redis: {
        CLEAR_DATA_IN_REDIS: `/v1/general/redis/clear-all-data`,
        CLEAR_PROJECT_DATA_IN_REDIS: (idPath: string) => `/v1/general/redis/clear-project-data/?id_path=${encodeURIComponent(idPath)}`,
    },

    // User Management
    users: {
        GET_ALL_USERS: "/v1/general/users",
        GET_ALL_PROJECT_USERS: (id_path: string) => withIdPath("/v1/projects/users", id_path),
        GET_PROJECT_USER_BY_ID: (id: number, id_path: string) => withIdPath(`/v1/projects/users/${id}`, id_path),
        GET_USER_BY_ID: (id: number) => `/v1/general/users/${id}`,
        CREATE_USER: "/v1/general/users",
        UPDATE_USER: (id: number) => `/v1/general/users/${id}`,
        DELETE_USER: (id: number) => `/v1/general/users/${id}`,
        SEARCH_USERS: "/v1/general/users/search",
        GET_USER_PERMISSIONS: (id: number) => `/v1/general/users/${id}/permissions`,
        GET_USER_GROUPS: (id: number) => `/v1/general/users/${id}/groups`,
    },

    // Permission Groups Management
    permissionGroups: {
        GET_ALL_GROUPS: "/v1/general/permission-groups",
        GET_GROUP_BY_ID: (id: number) => `/v1/general/permission-groups/${id}`,
        CREATE_GROUP: "/v1/general/permission-groups",
        UPDATE_GROUP: (id: number) => `/v1/general/permission-groups/${id}`,
        DELETE_GROUP: (id: number) => `/v1/general/permission-groups/${id}`,
        GET_GROUP_USERS: (id: number) => `/v1/general/permission-groups/${id}/users`,
        GET_GROUP_PERMISSIONS: (id: number) => `/v1/general/iam/groups/${id}/permissions`,
        ADD_USERS_TO_GROUP: (id: number) => `/v1/general/permission-groups/${id}/users`,
        REMOVE_USER_FROM_GROUP: (groupId: number, userId: number) => `/v1/general/permission-groups/${groupId}/users/${userId}`,
        ADD_PERMISSIONS_TO_GROUP: (id: number) => `/v1/general/permission-groups/${id}/permissions`,
        REMOVE_PERMISSION_FROM_GROUP: (groupId: number, permissionId: number) => `/v1/general/permission-groups/${groupId}/permissions/${permissionId}`,
    },

    // API Permissions Management
    permissions: {
        GET_ALL_PERMISSIONS: "/v1/general/api-permissions",
        GET_PERMISSION_BY_ID: (id: number) => `/v1/general/api-permissions/${id}`,
        CREATE_PERMISSION: "/v1/general/api-permissions",
        UPDATE_PERMISSION: (id: number) => `/v1/general/api-permissions/${id}`,
        DELETE_PERMISSION: (id: number) => `/v1/general/api-permissions/${id}`,
        SEARCH_PERMISSIONS: "/v1/general/api-permissions/search",
        GET_PERMISSIONS_BY_METHOD: (method: string) => `/v1/general/api-permissions/method/${method}`,
    },
    // Two-Factor Authentication
    twoFactor: {
        GET_STATUS: "/v1/general/2fa/status",
        SETUP_EMAIL: "/v1/general/2fa/setup/email",
        SETUP_AUTHENTICATOR: "/v1/general/2fa/setup/authenticator",
        VERIFY_SETUP: "/v1/general/2fa/setup/verify",
        SEND_EMAIL_OTP: "/v1/general/2fa/setup/send-otp",
        VERIFY_OTP: "/v1/general/2fa/login/verify",
        DISABLE: "/v1/general/2fa/disable",
        REGENERATE_BACKUP_CODES: "/v1/general/2fa/backup-codes",
    },

    // Embed Key — Public verify endpoint (không cần JWT)
    // Backend: GET /v1/embed/verify?key={id_path}|{token}
    embed: {
        VERIFY: (compositeKey: string) => `/v1/embed/verify?key=${encodeURIComponent(compositeKey)}`,
    },

    customerIdentity: {
        CAPTURE: (id_path?: string) => withIdPath('/v1/projects/customers/capture', id_path),
        ACCESS_LOG: (id_path?: string) => withIdPath('/v1/projects/customers/access-log', id_path),
    },

    platformProjects: {
        GET_ALL: "/v1/general/platform-projects",
        GET_UNMAPPED: "/v1/general/platform-projects/unmapped",
        CREATE: "/v1/general/platform-projects",
        PROMOTE: "/v1/general/platform-projects/promote",
        UPDATE: (id: string | number) => `/v1/general/platform-projects?id=${encodeURIComponent(String(id))}`,
        DELETE: (id: string | number) => `/v1/general/platform-projects?id=${encodeURIComponent(String(id))}`,
    },

    agent3dModelsGeneral: {
        LIST: (page: number, limit: number, search: string = '', is_active?: boolean) => {
            let url = `/v1/general/agent-3d-models?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`;
            if (is_active !== undefined) url += `&is_active=${is_active}`;
            return url;
        },
        DETAIL: (id: string | number) => `/v1/general/agent-3d-models/${id}`,
        CREATE: "/v1/general/agent-3d-models",
        UPDATE: (id: string | number) => `/v1/general/agent-3d-models/${id}`,
        DELETE: (id: string | number) => `/v1/general/agent-3d-models/${id}`,
        PROJECTS_LIST: (page: number, limit: number, search: string = '') =>
            `/v1/general/agent-3d-models/projects/list?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`,
        PROJECT_MODELS: (projectId: string | number) => `/v1/general/agent-3d-models/projects/${projectId}/models`,
        ASSIGN: "/v1/general/agent-3d-models/projects/assign",
        UNASSIGN: (modelId: string | number, projectId: string | number) =>
            `/v1/general/agent-3d-models/${modelId}/projects/${projectId}`,
    },

    // API Request Tracing
    apiRequestTracing: {
        LIST: "/v1/general/api-request-tracing",
        BY_PROJECT: (projectId: string) => `/v1/general/api-request-tracing/project/${encodeURIComponent(projectId)}`,
        CLEANUP: "/v1/general/api-request-tracing/cleanup",
    },

    contact: {
        GET_ALL_CONTACTS: (id_path: string) => withIdPath(`/v1/projects/contact`, id_path),
        GET_ALL_CONTACT_BY_SUBLINK: ({ id_path, sublink }: { id_path: string; sublink: string }) => withIdPath(`/v1/projects/contact/by-sublink?sublink=${sublink}`, id_path),
        CREATE_CONTACT: (id_path: string) => withIdPath(`/v1/projects/contact`, id_path),
        UPDATE_CONTACT: (id: string | number, id_path: string) => withIdPath(`/v1/projects/contact/${id}`, id_path),
        DELETE_CONTACT: (id: string | number, id_path: string) => withIdPath(`/v1/projects/contact/${id}`, id_path),
        SET_AI_FALLBACK: (id: string | number, id_path: string) => withIdPath(`/v1/projects/contact/${id}/fallback`, id_path),
    },

    auditLogs: {
        GET_ALL_GENERAL: "/v1/general/audit-logs",
        GET_BY_ID_GENERAL: (event_id: string) => `/v1/general/audit-logs/${event_id}`,
        DELETE_GENERAL: (event_id: string) => `/v1/general/audit-logs/${event_id}`,
        PURGE_GENERAL: "/v1/general/audit-logs/purge",
        BACKUP_GENERAL: "/v1/general/audit-logs/backup",
        RESTORE_GENERAL: "/v1/general/audit-logs/restore",
        
        GET_ALL_PROJECT: (id_path: string) => withIdPath(`/v1/projects/audit-logs`, id_path),
        GET_BY_ID_PROJECT: (event_id: string, id_path: string) => withIdPath(`/v1/projects/audit-logs/${event_id}`, id_path),
        DELETE_PROJECT: (event_id: string, id_path: string) => withIdPath(`/v1/projects/audit-logs/${event_id}`, id_path),
        PURGE_PROJECT: (id_path: string) => withIdPath(`/v1/projects/audit-logs/purge`, id_path),
        BACKUP_PROJECT: (id_path: string) => withIdPath(`/v1/projects/audit-logs/backup`, id_path),
        RESTORE_PROJECT: (id_path: string) => withIdPath(`/v1/projects/audit-logs/restore`, id_path),
    },

    pointCloudPublic: {
        // Dedicated public, unauthenticated endpoint (Phase 1) — never reuse the admin
        // point-cloud endpoint here, it leaks raw_s3_key/error_message.
        LIST_READY_DATASETS: (id_path: string) => `/v1/public/projects/${id_path}/point-clouds`,
    },

    // Project Menu Management APIs (Dynamic Sidebar from sg3d_menu table)
    projectMenu: {
        GET_TREE: (id_path: string) => `/v1/projects/menu/tree?id_path=${encodeURIComponent(id_path)}`,
        GET_LIST: (id_path: string) => `/v1/projects/menu/list?id_path=${encodeURIComponent(id_path)}`,
        GET_ITEM: (id: string, id_path: string) => `/v1/projects/menu/item/${encodeURIComponent(id)}?id_path=${encodeURIComponent(id_path)}`,
        CREATE_ITEM: (id_path: string) => `/v1/projects/menu/item?id_path=${encodeURIComponent(id_path)}`,
        UPDATE_ITEM: (id: string, id_path: string) => `/v1/projects/menu/item/${encodeURIComponent(id)}?id_path=${encodeURIComponent(id_path)}`,
        DELETE_ITEM: (id: string, id_path: string) => `/v1/projects/menu/item/${encodeURIComponent(id)}?id_path=${encodeURIComponent(id_path)}`,
        SAVE_TREE: (id_path: string) => `/v1/projects/menu/tree?id_path=${encodeURIComponent(id_path)}`,
        REORDER: (id_path: string) => `/v1/projects/menu/reorder?id_path=${encodeURIComponent(id_path)}`,
        RESET: (id_path: string) => `/v1/projects/menu/reset?id_path=${encodeURIComponent(id_path)}`,
    },
}

export default apiEndPointList;
